import {
  Tooth,
  ArcadaType,
  OdontogramState,
  ToothCondition,
  PaintCondition,
  conditionConfig,
  OdontogramAction,
} from './odontogram.types';
import { IDentalTreatmentItem } from '../../shared/interfaces';

const createTooth = (
  id: number,
  numTooth: number,
  type: ArcadaType,
): Tooth => ({
  id,
  numTooth,
  type,
  updated: 0,
  currentState: 0,
  face: '',
  classes: {
    class1: 'dente',
    class2: 'dente',
    class3: 'dente',
    class4: 'dente',
    class5: 'dente',
    class6: 'ausente',
    class7: 'ausente',
    class8: 'coroa',
    class9: 'endodoncia',
    class10: 'implante',
  },
});

const generateArcade = (
  idInicio: number,
  totalDentes: number,
  tipo: ArcadaType,
  calculoNumDente: (index: number) => number,
): Tooth[] => {
  return Array.from({ length: totalDentes }, (_, i) => {
    const id = idInicio + i;
    const numDente = calculoNumDente(i);
    return createTooth(id, numDente, tipo);
  });
};

export const carregarDentesIniciais = (): OdontogramState => {
  const adultoCima = generateArcade(1, 16, 'adultoCima', (i) =>
    i < 8 ? 18 - i : i + 13,
  );
  const adultoBaixo = generateArcade(17, 16, 'adultoBaixo', (i) =>
    i < 8 ? 48 - i : 31 + (i - 8),
  );
  const criancaCima = generateArcade(33, 10, 'criancaCima', (i) =>
    i < 5 ? 55 - i : i + 56,
  );
  const criancaBaixo = generateArcade(43, 10, 'criancaBaixo', (i) =>
    i < 5 ? 85 - i : 71 + (i - 5),
  );

  return { adultoCima, adultoBaixo, criancaCima, criancaBaixo };
};

export const getFaceTooth = (
  tooth: Tooth,
  value: number,
  conditionSelected: number,
): string => {
  const { type, numTooth, id } = tooth;
  const faces: { [key: number]: string } = {};

  const conditionsToReturnEmpty = [
    ToothCondition.ENDODONTIA,
    ToothCondition.AUSENTE,
    ToothCondition.IMPLANTE,
    ToothCondition.COROA,
    ToothCondition.OUTROS,
  ];
  if (conditionsToReturnEmpty.includes(conditionSelected)) {
    return '';
  }

  if (type === 'adultoCima') {
    faces[1] = 'V';
    faces[3] = 'L/P';
    faces[2] = numTooth < 20 ? 'M' : 'D';
    faces[4] = numTooth < 20 ? 'D' : 'M';
    faces[5] = id >= 6 && id < 12 ? 'I' : 'O';
  } else if (type === 'adultoBaixo') {
    faces[1] = 'L/P';
    faces[3] = 'V';
    faces[2] = numTooth > 40 ? 'M' : 'D';
    faces[4] = numTooth > 40 ? 'D' : 'M';
    faces[5] = id >= 22 && id < 28 ? 'I' : 'O';
  } else if (type === 'criancaCima') {
    faces[1] = 'V';
    faces[3] = 'L/P';
    faces[2] = numTooth < 60 ? 'M' : 'D';
    faces[4] = numTooth < 60 ? 'D' : 'M';
    faces[5] = 'O';
  } else if (type === 'criancaBaixo') {
    faces[1] = 'L/P';
    faces[3] = 'V';
    faces[2] = numTooth > 80 ? 'M' : 'D';
    faces[4] = numTooth > 80 ? 'D' : 'M';
    faces[5] = 'O';
  }

  return faces[value] || '';
};

export const hideToothFaces = (tooth: Tooth): void => {
  for (let i = 1; i <= 5; i++) {
    tooth.classes[`class${i}`] = 'vazio';
  }
};

export const showToothFaces = (tooth: Tooth): void => {
  for (let i = 1; i <= 5; i++) {
    tooth.classes[`class${i}`] = 'dente';
  }
};

export const getFaceValueFromName = (faceName: string): number => {
  const faceMap: { [key: string]: number } = {
    V: 1,
    VESTIBULAR: 1,
    M: 2,
    MESIAL: 2,
    L: 3,
    LINGUAL: 3,
    P: 3,
    PALATINA: 3,
    D: 4,
    DISTAL: 4,
    O: 5,
    OCLUSAL: 5,
    I: 5,
    INCISAL: 5,
    GERAL: 9,
  };
  if (faceName === '') {
    return 9;
  }
  return faceMap[faceName.toUpperCase()] || 0;
};

export const paintOdontogramFromData = (
  items: IDentalTreatmentItem[],
  dispatch: React.Dispatch<OdontogramAction>,
) => {
  const arcadas: any = carregarDentesIniciais();

  items.forEach((item) => {
    if (!item.tooth_number || !item.current_state) {
      return;
    }

    let targetTooth: Tooth | undefined;
    let arcadaKey: string | undefined;
    let toothIndex = -1;

    for (const key in arcadas) {
      const idx = arcadas[key].findIndex(
        (d: Tooth) => String(d.numTooth) === String(item.tooth_number),
      );
      if (idx !== -1) {
        targetTooth = arcadas[key][idx];
        arcadaKey = key;
        toothIndex = idx;
        break;
      }
    }

    if (!targetTooth || !arcadaKey) {
      return;
    }

    const condicao = Number(item.current_state) as PaintCondition;
    const faceValor = getFaceValueFromName(item.face);
    const config = conditionConfig[condicao];

    const precisaDeFaceEspecifica = ![
      ToothCondition.AUSENTE,
      ToothCondition.IMPLANTE,
      ToothCondition.COROA,
      ToothCondition.ENDODONTIA,
      ToothCondition.OUTROS,
    ].includes(condicao);

    if (!config || (precisaDeFaceEspecifica && faceValor === 0)) {
      return;
    }

    const denteAtualizado: Tooth = {
      ...targetTooth,
      updated: 1,
      currentState: condicao,
      classes: { ...targetTooth.classes },
    };

    switch (condicao) {
      case ToothCondition.AUSENTE:
        hideToothFaces(denteAtualizado);
        denteAtualizado.classes['class6'] = config.classe;
        denteAtualizado.classes['class7'] = config.classe;
        break;
      case ToothCondition.IMPLANTE:
        hideToothFaces(denteAtualizado);
        denteAtualizado.classes['class10'] = config.classe;
        break;
      case ToothCondition.COROA:
      case ToothCondition.ENDODONTIA:
      case ToothCondition.OUTROS:
        showToothFaces(denteAtualizado);
        denteAtualizado.classes['class9'] = config.classe;
        break;
      default: {
        const classeKey = `class${faceValor}`;
        denteAtualizado.classes[classeKey] = config.classe;
        break;
      }
    }

    arcadas[arcadaKey][toothIndex] = denteAtualizado;
  });

  dispatch({ type: 'SET_ARCADAS', payload: arcadas });
};
